import axios from 'axios';
import { Feature, FeatureCollection } from 'geojson';
import mapData from '../data/countries.json';
import { Country, CountryStyle, RawCountryData, ProcessedCountryData } from '../types/Country';
import { 
    STATE_ACTIVE, 
    STATE_DEFAULT, 
    LANG_FI,
    LANG_ENG, 
    RISK_CLASS_A,
    RISK_CLASS_B,
    RISK_CLASS_C,
    RISK_CLASS_D,
    RISK_CLASS_E,
    RISK_CLASS_A_COLOR, 
    RISK_CLASS_B_COLOR, 
    RISK_CLASS_C_COLOR, 
    RISK_CLASS_D_COLOR, 
    RISK_CLASS_E_COLOR,
    RISK_CLASS_NONE_COLOR,
    siteLang,

} from './variables';
import * as FIELDS from './fields';
import { API_URL, API_KEY } from '../local.js';

export const fetchCountryData = async () => {
    let done = false;
    const completeData: RawCountryData[] = [];
    let apiUrlWithKey = `${API_URL}?api-key=${API_KEY}`;
    while (!done) {
        const res = await axios.get(apiUrlWithKey);
        const data = res.data.data;
        for (const dataObject of data) {
            if (dataObject.attributes) {
                completeData.push(dataObject.attributes)
            }
        }
        if (res.data.links.next) {
            const next = res.data.links.next.href;
            apiUrlWithKey = next;
        }
        else {
            done = true;
        }
    }
    return completeData;
}

export const loadMapData = () => {
    return mapData as FeatureCollection;
}

const getFeatures = (data: FeatureCollection) => {
    return data.features;
}

// Return type: Promise<Array<MergedCountry>>
// Linter keeps screaming because of that one if check.
// There are going to be undefined things in the data and thats it.
export const processData = async (): Promise<any> => {
    const countryData = await fetchCountryData();
    const processedCountryData = countryData?.map(rawCountry => {
        return convertRawCountry(rawCountry);
    });
    const mapData = loadMapData();
    const features = getFeatures(mapData);
    const result = features.map(feature => {
        const matchingCountry = processedCountryData.filter(processedCountry => {
            if (feature && feature.properties && feature.properties.ADM0_A3 && feature.properties.ADMIN) {
                return (feature.properties.ADM0_A3 === processedCountry.countryId || feature.properties.ADMIN === processedCountry.nameEng);
            }
        })[0];
        if (matchingCountry) {
            return mergeGeoJsonWithCountry(matchingCountry, feature);
        }
    });
    return result.filter(resultObject => {
        return resultObject !== undefined;
    });
}

const mergeGeoJsonWithCountry = (countryData: ProcessedCountryData, feature: Feature): Country => {
    const mergedCountry = {
        ...countryData
    } as Country;
    mergedCountry.geoJson = feature;
    mergedCountry.state = STATE_DEFAULT;
    // Style
    const fillColor = getColorForRiskClass(countryData.riskClass);
    const defaultStyle = {
        fillColor: fillColor,
        fillOpacity: 0.7,
        color: 'black',
        weight: 1,
    } as CountryStyle;
    mergedCountry.defaultStyle = defaultStyle;
    const activeStyle = {
        fillColor: fillColor,
        fillOpacity: 1,
        color: 'white',
        weight: 3,
    } as CountryStyle;
    mergedCountry.activeStyle = activeStyle;
    const inActiveStyle = {
        fillColor: 'white',
        fillOpacity: 0.7,
        color: 'black',
        weight: 1,
    } as CountryStyle;
    mergedCountry.inactiveStyle = inActiveStyle;
    return mergedCountry;
}

export const getCountryById = (data: Country[], id: string): Country | null => {
    const matchingCountry = data.filter(country => {
        return country.countryId === id.toUpperCase();
    })[0];
    return matchingCountry || null;
}

export const getCountryByName = (data: Country[], name: string): Country => {
    const matchingData = data.filter(dataObject => {
        return dataObject.nameEng === name || dataObject.nameFin === name || dataObject.nameSwe === name;
    })[0];
    return matchingData;
}

export const getColorForRiskClass = (riskClass: string): string => {
    const riskClassToColor: any = {
      'A': RISK_CLASS_A_COLOR,
      'B': RISK_CLASS_B_COLOR,
      'C': RISK_CLASS_C_COLOR,
      'D': RISK_CLASS_D_COLOR,
      'E': RISK_CLASS_E_COLOR,
      '-': RISK_CLASS_NONE_COLOR,
    };
    return riskClassToColor[riskClass];
}

const parseCoverPolicy = (policy: string): string => {
    const value = policy.charAt(0);
    const allowed = ['0', '1', '2', '3', '4', '5', '6', '7'];
    if (allowed.includes(value)) {
        return value
    }
    else return '-';
}

const parseRiskClass = (riskClass: string): string => {
    const allowed = ['A', 'B', 'C', 'D', 'E', '-'];
    if (allowed.includes(riskClass)) {
        return riskClass;
    }
    else return '-';
}

const parseConditions = (conditions: string): string => {
    if (conditions !== 'NULL') {
        return conditions;
    }
    else return '';
}

const convertRawCountry = (rawCountry: RawCountryData): ProcessedCountryData => {
    const processedCountry: ProcessedCountryData = {
        [FIELDS.CREATED_AT]: new Date(rawCountry[FIELDS.API_CREATED]),
        [FIELDS.LAST_MODIFIED]: new Date(rawCountry[FIELDS.API_LAST_MODIFIED]),
        [FIELDS.ENTITY_CREATED_AT]: new Date(rawCountry[FIELDS.API_ENTITY_CREATED_AT]),
        [FIELDS.COVER_POLICY_FIN]: parseConditions(rawCountry[FIELDS.API_COVER_POLICY_FIN]),
        [FIELDS.COVER_POLICY_ENG]: parseConditions(rawCountry[FIELDS.API_COVER_POLICY_ENG]),
        [FIELDS.CLASS_FIN]: parseCoverPolicy(rawCountry[FIELDS.API_CLASS_FIN]),
        [FIELDS.CLASS_ENG]: parseCoverPolicy(rawCountry[FIELDS.API_CLASS_ENG]),
        [FIELDS.RISK_CLASS]: parseRiskClass(rawCountry[FIELDS.API_RISK_CLASS]),
        [FIELDS.BANK_SPECIALIST]: rawCountry[FIELDS.API_BANK_SPECIALIST],
        [FIELDS.BANK_SPECIALIST_EMAIL]: rawCountry[FIELDS.API_BANK_SPECIALIST_EMAIL],
        [FIELDS.BANK_SPECIALIST_PHONE]: rawCountry[FIELDS.API_BANK_SPECIALIST_PHONE],
        [FIELDS.COUNTRY_SPECIALIST]: rawCountry[FIELDS.API_COUNTRY_SPECIALIST],
        [FIELDS.COUNTRY_SPECIALIST_EMAIL]: rawCountry[FIELDS.API_COUNTRY_SPECIALIST_EMAIL],
        [FIELDS.COUNTRY_SPECIALIST_PHONE]: rawCountry[FIELDS.API_COUNTRY_SPECIALIST_PHONE],
        [FIELDS.NAME_FIN]: rawCountry[FIELDS.API_NAME_FIN],
        [FIELDS.NAME_ENG]: rawCountry[FIELDS.API_NAME_ENG],
        [FIELDS.NAME_SWE]: rawCountry[FIELDS.API_NAME_SWE],
        [FIELDS.RISK_AREA]: rawCountry[FIELDS.API_RISK_AREA],
        [FIELDS.RISK_AREA_NAME_FIN]: rawCountry[FIELDS.API_RISK_AREA_NAME_FIN].replace('\t',''),
        [FIELDS.RISK_AREA_NAME_ENG]: rawCountry[FIELDS.API_RISK_AREA_NAME_ENG].replace('\t',''),
        [FIELDS.COUNTRY_ID]: rawCountry[FIELDS.API_COUNTRY_ID],
    };
    return processedCountry;
}

/**
 * This function is used to put together the list of countries in the filter search bar.
 * Returns the translated name of the country according to site language.
 * 
 * @param country
 *  Country in question.
 * @returns
 *  Translated name of the country.
 */
 export const getCountryNameByLang = (country: Country | ''): string => {
    if (country) {
        switch (siteLang) {
            case LANG_FI:
              return country.nameFin;
            case LANG_ENG:
              return country.nameEng;
            default:
              return country.nameEng;
          }
    }
    return '';
}

/**
 * This function is used to put together the list of continents in the filter 'select continent' dropdown.
 * Returns the translated name of the continent of the given country according to site language.
 * 
 * @param country
 *  Country in question.
 * @returns
 *  Translated name of the continent.
 */
export const getContinentByLang = (country: Country): string => {
    switch (siteLang) {
        case LANG_FI:
          return country.riskAreaNameFin;
        case LANG_ENG:
          return country.riskAreaNameEng;
        default:
          return country.nameEng;
      }
}

export const getTermsByLang = (country: Country): string => {
    switch (siteLang) {
        case LANG_FI:
          return country.coverPolicyFin;
        case LANG_ENG:
          return country.coverPolicyEng;
        default:
          return country.coverPolicyEng;
      }
}

export const getSelectedCountry = (state: Country[]): Country | null => {
    const country = state.filter(country => {
        return country.state === STATE_ACTIVE;
    })[0];
    if (country) {
        return country;
    }
    else return null;
}

export const getTranslatedRiskClassValue = (riskClass: string) => {
    switch (riskClass) {
        case RISK_CLASS_A: {
            return 'countryData:policy.noRestriction';
        }
        case RISK_CLASS_B: {
            return 'countryData:policy.flexibleLandPolicy';
        }
        case RISK_CLASS_C: {
            return 'countryData:policy.restrictiveLandPolicy';
        }
        case RISK_CLASS_D: {
            return 'countryData:policy.specificRestrictions';
        }
        case RISK_CLASS_E: {
            return 'countryData:policy.offCover'
        }
        default: {
            return 'countryData:policy.undefined';
        }
    }
}

export const getTranslatedValueForPolicy = (policy: string) => {
    switch (policy) {
        case '0': {
            return 'countryData:classification.economy';
        }
        case'1': {
            return 'countryData:classification.solvencyExcellent';
        }
        case '2': {
            return 'countryData:classification.solvencyGood';
        }
        case '3': {
            return 'countryData:classification.solvencySufficient';
        }
        case '4': {
            return 'countryData:classification.solvencyModerate';
        }
        case '5': {
            return 'countryData:classification.solvencyMediocre';
        }
        case '6': {
            return 'countryData:classification.solvencyWeak';
        }
        case '7': {
            return 'countryData:classification.solvencyPoor';
        }
        case '-': {
            return 'countryData:classification.undefined';
        }
        default: {
            return 'countryData:classification.undefined';
        }
    }
}
